import Button from "../../../../ui/Button/Button";
import { Close, Mail, Plus } from "../../../../ui/Icon/Icon";
import styles from "./InviteTeamMembers.module.scss";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import IconButton from "../../../../ui/IconButton/IconButton";
import { zodResolver } from "@hookform/resolvers/zod";
import { graphql } from "../../../../__generated__/gql";
import { useMutation } from "urql";
import TextInput from "../../../../ui/TextInput/TextInput";
import { useToast } from "../../../../ui/Toast/ToastContext";
import Feedback from "../../../../ui/Feedback/Feedback";

const InviteUsersMutation = graphql(/* GraphQL */ `
  mutation inviteUsers($input: InviteUsersInput!) {
    inviteUsers(input: $input) {
      id
    }
  }
`);

const schema = z.object({
  users: z.array(
    z.object({
      email: z.string().email(),
    }),
  ),
});

type InviteTeamMembersFormData = z.infer<typeof schema>;

function InviteTeamMembers() {
  const { showToast } = useToast();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty },
    reset,
  } = useForm<InviteTeamMembersFormData>({
    resolver: zodResolver(schema),
    reValidateMode: "onChange",
    defaultValues: {
      users: [{ email: "" }],
    },
  });
  const [, inviteUsers] = useMutation(InviteUsersMutation);

  const { fields, append, remove } = useFieldArray({ control, name: "users" });

  const onSubmit = async (data: InviteTeamMembersFormData) => {
    const result = await inviteUsers({
      input: {
        users: data.users.map((user) => ({
          email: user.email,
        })),
      },
    });

    if (!result.error) {
      reset({ users: [{ email: "" }] });
      showToast({
        title: `${data.users.length} ${
          data.users.length === 1 ? "invite" : "invites"
        } sent`,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.container}>
      <div className={styles.inputs}>
        {fields.map((field, index) => (
          <Controller
            key={field.id}
            control={control}
            name={`users.${index}.email`}
            render={({ field, fieldState: { error } }) => (
              <div>
                <div className={styles.inputRow}>
                  <TextInput
                    {...field}
                    onBlur={(e) => {
                      field.onChange(e.target.value.trim());
                      field.onBlur();
                    }}
                    error={!!error}
                    placeholder="you@example.com"
                    className={styles.input}
                    leftSection={<Mail size="medium" />}
                  />

                  {(index !== 0 || fields.length > 1) && (
                    <IconButton
                      aria-label="Remove"
                      IconComponent={Close}
                      onClick={() => remove(index)}
                    />
                  )}
                </div>
                {error?.message && (
                  <Feedback className={styles.feedback}>
                    {error.message}
                  </Feedback>
                )}
              </div>
            )}
          />
        ))}
      </div>
      <div className={styles.buttons}>
        <Button
          variant="link"
          leftIcon={<Plus size="medium" />}
          onClick={() => append({ email: "" })}
          disabled={isSubmitting}
        >
          Add another
        </Button>
        <Button
          variant="primary"
          leftIcon={<Mail size="medium" />}
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting || !isDirty}
        >
          Send invite
        </Button>
      </div>
    </form>
  );
}

export default InviteTeamMembers;
